import { useEffect, useRef, useState } from "react";
import { styled } from "styled-components";
import { motion } from "framer-motion";

import SivaSignature from "./SivaSignature";

const revealEase = [0.76, 0, 0.24, 1];

const transitionConfig = {
  intro: {
    initialY: "0%",
    animateY: ["0%", "0%", "-100%"],
    duration: 4.8,
    times: [0, 0.84, 1],
    coverAt: 0,
  },
  route: {
    initialY: "100%",
    animateY: ["100%", "0%", "0%", "-100%"],
    duration: 0.82,
    times: [0, 0.24, 0.58, 1],
    coverAt: 0.24,
  },
};

let activeLocks = 0;
let lockedScrollY = 0;

const scrollKeys = new Set([
  " ",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "End",
  "Home",
  "PageDown",
  "PageUp",
]);

function preventScroll(event) {
  event.preventDefault();
}

function preventScrollKeys(event) {
  if (scrollKeys.has(event.key)) {
    event.preventDefault();
  }
}

function holdScrollPosition() {
  if (window.scrollY !== lockedScrollY) {
    window.scrollTo({
      top: lockedScrollY,
      left: 0,
      behavior: "instant",
    });
  }
}

function updateLockedScrollPosition(event) {
  lockedScrollY =
    typeof event.detail?.top === "number" ? event.detail.top : window.scrollY;
}

function lockScroll() {
  if (activeLocks === 0) {
    lockedScrollY = window.scrollY;

    window.addEventListener("wheel", preventScroll, {
      passive: false,
    });

    window.addEventListener("touchmove", preventScroll, {
      passive: false,
    });

    window.addEventListener("keydown", preventScrollKeys);

    window.addEventListener("scroll", holdScrollPosition, {
      passive: true,
    });

    window.addEventListener(
      "page-transition:update-scroll-lock",
      updateLockedScrollPosition,
    );
  }

  activeLocks += 1;
}

function unlockScroll() {
  activeLocks = Math.max(0, activeLocks - 1);

  if (activeLocks === 0) {
    window.removeEventListener("wheel", preventScroll);
    window.removeEventListener("touchmove", preventScroll);
    window.removeEventListener("keydown", preventScrollKeys);
    window.removeEventListener("scroll", holdScrollPosition);
    window.removeEventListener(
      "page-transition:update-scroll-lock",
      updateLockedScrollPosition,
    );
  }
}

export default function PageTransition({ onCovered, variant = "route" }) {
  const isIntro = variant === "intro";
  const config = transitionConfig[variant] ?? transitionConfig.route;
  const covered = useRef(false);

  const [active, setActive] = useState(true);

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    lockScroll();

    return () => {
      unlockScroll();
    };
  }, [active]);

  useEffect(() => {
    if (typeof onCovered !== "function") {
      return undefined;
    }

    const coverDelay = config.duration * config.coverAt * 1000;

    const coverTimer = window.setTimeout(() => {
      covered.current = true;
      onCovered();
    }, coverDelay);

    return () => {
      window.clearTimeout(coverTimer);
    };
  }, [config.coverAt, config.duration, onCovered]);

  return (
    <TransitionLayer
      initial={{
        y: config.initialY,
      }}
      animate={{
        y: config.animateY,
      }}
      exit={{
        y: "-100%",
      }}
      transition={{
        duration: config.duration,
        times: config.times,
        ease: revealEase,
      }}
      onAnimationComplete={() => {
        if (!covered.current && typeof onCovered === "function") {
          covered.current = true;
          onCovered();
        }

        if (isIntro) {
          window.__introTransitionDone = true;
          window.dispatchEvent(new Event("intro-transition:complete"));
        } else {
          window.dispatchEvent(new Event("page-transition:complete"));
        }

        setActive(false);
      }}
      aria-hidden={!active}
    >
      {isIntro && (
        <motion.div
          className="signature"
          initial={{
            opacity: 1,
          }}
          animate={{
            opacity: [1, 1, 0],
          }}
          transition={{
            duration: 4,
            delay: 0.35,
            times: [0, 0.82, 1],
            ease: revealEase,
          }}
        >
          <SivaSignature color="#ffffff" />
        </motion.div>
      )}
    </TransitionLayer>
  );
}

const TransitionLayer = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 9999;

  width: 100%;
  height: 100vh;

  display: flex;
  align-items: center;
  justify-content: center;

  background: rgb(15, 15, 15);
  pointer-events: none;
  will-change: transform;

  .signature {
    width: clamp(160px, 22vw, 300px);

    display: block;
    will-change: opacity;
  }

  .signature svg {
    display: block;
    width: 100%;
    height: auto;
  }

  @media (max-width: 640px) {
    .signature {
      width: clamp(150px, 50vw, 230px);
    }
  }
`;
